// Schemas zod dos forms do painel admin de White Labels (wave 2).
// Espelham as validações do backoffice (AdminWhiteLabelUpdateRequest
// e AdminWhiteLabelConfigurationUpdateRequest): obrigatoriedades,
// tamanhos máximos e o pattern de cor hex (#RRGGBB).
import { z } from "zod"

import type {
  AdminWhiteLabelConfigurations,
  AdminWhiteLabelDetail,
} from "@/types/admin-white-labels"

// Pattern do backend (AdminWhiteLabelColorsDto).
export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/

// Cor no form: hex válido OU vazio ("" = não definida → null no
// payload, semântica merge do backend).
const hexColorOrEmpty = (message: string) =>
  z
    .string()
    .regex(HEX_COLOR_PATTERN, message)
    .or(z.literal(""))

// Form da tab Dados & Tema — name/description/slug obrigatórios
// (limites do AdminWhiteLabelUpdateRequest), toggles e as 5 cores.
export const buildThemeFormSchema = (messages: {
  required: string
  invalidHex: string
}) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, messages.required)
      .max(255),
    description: z
      .string()
      .trim()
      .min(1, messages.required)
      .max(255),
    slug: z
      .string()
      .trim()
      .min(1, messages.required)
      .max(50),
    principal: z.boolean(),
    active: z.boolean(),
    colors: z.object({
      lineBackground: hexColorOrEmpty(messages.invalidHex),
      buttonActionBackground: hexColorOrEmpty(
        messages.invalidHex
      ),
      buttonActionBackgroundHover: hexColorOrEmpty(
        messages.invalidHex
      ),
      buttonActionText: hexColorOrEmpty(
        messages.invalidHex
      ),
      background: hexColorOrEmpty(messages.invalidHex),
    }),
  })

export type ThemeFormValues = z.infer<
  ReturnType<typeof buildThemeFormSchema>
>

// defaultValues do form a partir do detalhe (cores null → "").
export const themeFormDefaults = (
  detail: AdminWhiteLabelDetail
): ThemeFormValues => ({
  name: detail.name,
  description: detail.description ?? "",
  slug: detail.slug,
  principal: detail.principal,
  active: detail.active,
  colors: {
    lineBackground: detail.colors?.lineBackground ?? "",
    buttonActionBackground:
      detail.colors?.buttonActionBackground ?? "",
    buttonActionBackgroundHover:
      detail.colors?.buttonActionBackgroundHover ?? "",
    buttonActionText: detail.colors?.buttonActionText ?? "",
    background: detail.colors?.background ?? "",
  },
})

// ── Configurações (tab Configurações) ───────────────────────────────

// Inteiro OBRIGATÓRIO ≥ 0 — os 10 @NotNull do backend incluem 5
// numéricos; "" NÃO pode virar 0 silenciosamente (preprocess →
// undefined → required_error).
const requiredNonNegativeInt = (message: string) =>
  z.preprocess(
    (value) =>
      value === "" || value === null ? undefined : value,
    z.coerce
      .number({
        required_error: message,
        invalid_type_error: message,
      })
      .int(message)
      .min(0, message)
  )

// Inteiro OPCIONAL ≥ 0 ("" = não informado).
const optionalNonNegativeInt = (message: string) =>
  z.preprocess(
    (value) =>
      value === "" || value === null || value === undefined
        ? undefined
        : value,
    z.coerce
      .number({ invalid_type_error: message })
      .int(message)
      .min(0, message)
      .optional()
  )

// Texto opcional com limite do backend ("" = não informado).
const optionalText = (max: number) =>
  z.string().trim().max(max)

// Form da tab Configurações — espelha o
// AdminWhiteLabelConfigurationUpdateRequest: 10 campos @NotNull
// (5 booleans de switch + 5 inteiros ≥ 0), limites de tamanho dos
// textos e o refund retry ANINHADO.
export const buildConfigurationsFormSchema = (messages: {
  required: string
  invalidNumber: string
}) =>
  z.object({
    // Assinatura & recorrência.
    subscriptionTotalRetry: requiredNonNegativeInt(
      messages.invalidNumber
    ),
    cancelAtLastRetry: z.boolean(),
    expireFirstCycle: z.boolean(),
    subscriptionAutoExpirationEnabled: z.boolean(),
    cycleAutoExpirationEnabled: z.boolean(),
    insuranceExpirationDays: requiredNonNegativeInt(
      messages.invalidNumber
    ),
    // E-mail.
    sendMailByRejectedTransaction: z.boolean(),
    showEmailParcelInfo: z.boolean(),
    emailSubjectPrefix: optionalText(50),
    // Checkout.
    newCheckoutEnabled: z.boolean(),
    checkoutCustom: z.boolean(),
    checkoutCustomHost: optionalText(255),
    checkoutDueDateMinOffsetDays: optionalNonNegativeInt(
      messages.invalidNumber
    ),
    checkoutDueDateMaxOffsetDays: optionalNonNegativeInt(
      messages.invalidNumber
    ),
    bypassLegalAge: requiredNonNegativeInt(
      messages.invalidNumber
    ),
    requireSecurityCode: z.boolean(),
    requireBirthDate: z.boolean(),
    restrictPaymentMethodChange: z.boolean(),
    // Boleto.
    enableBankSlipPdfPassword: z.boolean(),
    bankSlipInstructions: optionalText(255),
    daysPastDue: requiredNonNegativeInt(
      messages.invalidNumber
    ),
    // Transação.
    softDescriptorPrefix: optionalText(10),
    softDescriptorPattern: optionalText(100),
    usePay2bTransactionError: z.boolean(),
    orderCodePrefix: optionalText(10),
    orderSubscriptionCodePrefix: optionalText(10),
    sendCallbackInformation: z.boolean(),
    smartRouting: z.boolean(),
    // Reembolso (retry) — aninhado, como no contrato.
    orderRefundRetryConfiguration: z.object({
      enabled: z.boolean(),
      maxAttempts: optionalNonNegativeInt(
        messages.invalidNumber
      ),
      intervalDays: optionalNonNegativeInt(
        messages.invalidNumber
      ),
      codes: optionalText(100),
    }),
  })

export type ConfigurationsFormValues = z.infer<
  ReturnType<typeof buildConfigurationsFormSchema>
>

// defaultValues do form — dado do GET ou, no 404 (não configurado),
// defaults sensatos (0/false/""; refund retry com os defaults do
// backend: 3 tentativas a cada 2 dias, desligado).
export const configurationsFormDefaults = (
  data: AdminWhiteLabelConfigurations | null | undefined
): ConfigurationsFormValues => ({
  subscriptionTotalRetry: data?.subscriptionTotalRetry ?? 0,
  cancelAtLastRetry: data?.cancelAtLastRetry ?? false,
  expireFirstCycle: data?.expireFirstCycle ?? false,
  subscriptionAutoExpirationEnabled:
    data?.subscriptionAutoExpirationEnabled ?? false,
  cycleAutoExpirationEnabled:
    data?.cycleAutoExpirationEnabled ?? false,
  insuranceExpirationDays:
    data?.insuranceExpirationDays ?? 0,
  sendMailByRejectedTransaction:
    data?.sendMailByRejectedTransaction ?? false,
  showEmailParcelInfo: data?.showEmailParcelInfo ?? false,
  emailSubjectPrefix: data?.emailSubjectPrefix ?? "",
  newCheckoutEnabled: data?.newCheckoutEnabled ?? false,
  checkoutCustom: data?.checkoutCustom ?? false,
  checkoutCustomHost: data?.checkoutCustomHost ?? "",
  checkoutDueDateMinOffsetDays:
    data?.checkoutDueDateMinOffsetDays ?? undefined,
  checkoutDueDateMaxOffsetDays:
    data?.checkoutDueDateMaxOffsetDays ?? undefined,
  bypassLegalAge: data?.bypassLegalAge ?? 0,
  requireSecurityCode: data?.requireSecurityCode ?? false,
  requireBirthDate: data?.requireBirthDate ?? false,
  restrictPaymentMethodChange:
    data?.restrictPaymentMethodChange ?? false,
  enableBankSlipPdfPassword:
    data?.enableBankSlipPdfPassword ?? false,
  bankSlipInstructions: data?.bankSlipInstructions ?? "",
  daysPastDue: data?.daysPastDue ?? 0,
  softDescriptorPrefix: data?.softDescriptorPrefix ?? "",
  softDescriptorPattern: data?.softDescriptorPattern ?? "",
  usePay2bTransactionError:
    data?.usePay2bTransactionError ?? false,
  orderCodePrefix: data?.orderCodePrefix ?? "",
  orderSubscriptionCodePrefix:
    data?.orderSubscriptionCodePrefix ?? "",
  sendCallbackInformation:
    data?.sendCallbackInformation ?? false,
  smartRouting: data?.smartRouting ?? false,
  orderRefundRetryConfiguration: {
    enabled:
      data?.orderRefundRetryConfiguration?.enabled ?? false,
    maxAttempts:
      data?.orderRefundRetryConfiguration?.maxAttempts ?? 3,
    intervalDays:
      data?.orderRefundRetryConfiguration?.intervalDays ??
      2,
    codes: data?.orderRefundRetryConfiguration?.codes ?? "",
  },
})

// Form → payload do PUT: "" vira null (campo não informado) e os
// inteiros opcionais ausentes vão como null.
export const toConfigurationsPayload = (
  values: ConfigurationsFormValues
): AdminWhiteLabelConfigurations => ({
  ...values,
  emailSubjectPrefix: values.emailSubjectPrefix || null,
  checkoutCustomHost: values.checkoutCustomHost || null,
  checkoutDueDateMinOffsetDays:
    values.checkoutDueDateMinOffsetDays ?? null,
  checkoutDueDateMaxOffsetDays:
    values.checkoutDueDateMaxOffsetDays ?? null,
  bankSlipInstructions: values.bankSlipInstructions || null,
  softDescriptorPrefix: values.softDescriptorPrefix || null,
  softDescriptorPattern:
    values.softDescriptorPattern || null,
  orderCodePrefix: values.orderCodePrefix || null,
  orderSubscriptionCodePrefix:
    values.orderSubscriptionCodePrefix || null,
  orderRefundRetryConfiguration: {
    enabled: values.orderRefundRetryConfiguration.enabled,
    maxAttempts:
      values.orderRefundRetryConfiguration.maxAttempts ??
      null,
    intervalDays:
      values.orderRefundRetryConfiguration.intervalDays ??
      null,
    codes:
      values.orderRefundRetryConfiguration.codes || null,
  },
})

/** Mesma faixa validada no gateway — o formulário recusa antes de ir na rede. */
export const MIN_EXPIRATION_SECONDS = 60
export const MAX_EXPIRATION_SECONDS = 86400
export const DEFAULT_EXPIRATION_SECONDS = 7200

/**
 * O nome vira o `clientId` (`APP-<NOME>`) no gateway e é UNIQUE na tabela, então o charset é
 * restrito: espaço ou acento produziriam um clientId que o parceiro não consegue digitar.
 */
export const APP_CREDENTIAL_NAME_PATTERN = /^[A-Za-z0-9-]+$/
export const APP_CREDENTIAL_NAME_MAX_LENGTH = 100

export const buildAppCredentialFormSchema = (messages: {
  nameRequired: string
  nameTooLong: string
  nameInvalid: string
  expirationRange: string
}) =>
  z.object({
    name: z
      .string()
      .trim()
      .min(1, messages.nameRequired)
      .max(
        APP_CREDENTIAL_NAME_MAX_LENGTH,
        messages.nameTooLong
      )
      .regex(
        APP_CREDENTIAL_NAME_PATTERN,
        messages.nameInvalid
      ),
    expirationTokenTime: z.coerce
      .number({
        invalid_type_error: messages.expirationRange,
      })
      .int(messages.expirationRange)
      .min(MIN_EXPIRATION_SECONDS, messages.expirationRange)
      .max(
        MAX_EXPIRATION_SECONDS,
        messages.expirationRange
      ),
  })

export type AppCredentialFormValues = z.infer<
  ReturnType<typeof buildAppCredentialFormSchema>
>
